import {
    useContext,
    type FC
} from "react";
import {
    Pressable,
    View
} from "react-native";
import type {
    DataTableSortButtonProps
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    ArrowUpDown as ArrowUpDownIcon,
    ArrowDown as ArrowDownIcon,
    ArrowUp as ArrowUpIcon
} from "lucide-react-native";
import {
    formatNumber
} from "../../../../helpers/locale";
import {
    TableContext
} from "../../../table/context";
import AnimatedIcon from "../../../animatedIcon";
import StateLayer from "../../../stateLayer";
import Text from "../../../text";

const DataTableSortButton: FC<DataTableSortButtonProps> = ({
    customTheme,
    direction,
    onPress,
    style,
    title,
    rank
}) => {
    const {
        shapeConfigs,
        touchTargets,
        typography,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const tableContext = useContext(TableContext);

    const pointerType = usePointerType();

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState();

    const headVariant = tableContext?.measures.headVariant ?? "labelMediumSize";

    const {
        container: containerDynamicStyle,
        surface: surfaceDynamicStyle
    } = useStyles({
        isFocusVisible,
        shapeConfigs,
        touchTargets,
        headVariant,
        pointerType,
        typography,
        radiuses,
        borders,
        colors,
        spaces
    });

    const isSorted = direction !== undefined;

    const SortIcon = direction === "asc" ? ArrowUpIcon : direction === "desc" ? ArrowDownIcon : ArrowUpDownIcon;

    return <Pressable
        {...interactionHandlers}
        accessibilityLabel={title}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        onPress={onPress}
        role="button"
    >
        <View
            style={[
                stylesheet.surface,
                surfaceDynamicStyle
            ]}
        >
            <StateLayer
                borderRadius={radiuses.actions}
                borderWidth={borders.line}
                customTheme={customTheme}
                overlayColor="default"
                isHovered={isHovered}
                isPressed={isPressed}
            />
            <Text
                color={isSorted || isHovered || isPressed ? "high" : "mid"}
                customTheme={customTheme}
                variant={headVariant}
                numberOfLines={1}
            >
                {title}
            </Text>
            <View
                style={stylesheet.icon}
                aria-hidden={true}
            >
                <AnimatedIcon
                    color={isSorted ? "high" : "low"}
                    customTheme={customTheme}
                    iconTransition="morph"
                    icon={SortIcon}
                    size="small"
                />
            </View>
            {
                rank ?
                    <Text
                        customTheme={customTheme}
                        variant="labelSmallSize"
                        color="mid"
                    >
                        {
                            formatNumber({
                                locale: activeLocale,
                                value: rank
                            })
                        }
                    </Text>
                :
                    null
            }
        </View>
    </Pressable>;
};
export default DataTableSortButton;
